import { toUnityFixed } from '../../../../helpers/unityHelper';
import { UnityEvent, waitForEventOnce } from '../../../../helpers/unityPubSub';
import {
  callUnityEventModifyProperty,
  enableGameObject,
  disableGameObject,
} from '../ProductBuilderComponents/commonHelper';
import { undoRedo } from './UndoRedo';

export class UndoRedoTape {
  constructor({ undoRedoInstance }) {
    this.undoRedo = undoRedoInstance;
    this.pending = new Map();
    this.createAction = this.undoRedo.createAction;
    this._init();
  }

  _init() {
    this._pushSingle = this.createAction(
      ({
        prevValue,
        nextValue,
        unityProp,
        objectName,
        componentType,
        sideLocation,
        index,
        updateCallback,
        isPosition,
      }) => {
        if (prevValue === nextValue) return false;
        const cType = componentType.charAt(0).toUpperCase() + componentType.slice(1);
        return {
          __ACTION__: 'TAPE_PROP_CHANGE',
          undo: () => {
            if (isPosition) {
              callUnityEventModifyProperty(
                { name: 'positionY', value: prevValue },
                'yTape',
                objectName,
                sideLocation || '',
                cType
              );
            } else {
              callUnityEventModifyProperty(prevValue, unityProp, objectName, sideLocation || '', cType);
            }
            updateCallback?.(index, unityProp, prevValue);
          },
          redo: () => {
            if (isPosition) {
              callUnityEventModifyProperty(
                { name: 'positionY', value: nextValue },
                'yTape',
                objectName,
                sideLocation || '',
                cType
              );
            } else {
              callUnityEventModifyProperty(nextValue, unityProp, objectName, sideLocation || '', cType);
            }
            updateCallback?.(index, unityProp, nextValue);
          },
        };
      }
    );

    this._pushMulti = this.createAction(
      ({ changes, objectName, componentType, sideLocation, index, updateCallback }) => {
        const hasChange = changes.some(c => c.prevValue !== c.nextValue);
        if (!hasChange) return false;
        const cType = componentType.charAt(0).toUpperCase() + componentType.slice(1);
        return {
          __ACTION__: 'TAPE_MULTI_PROP_CHANGE',
          undo: () => {
            changes.forEach(c => {
              callUnityEventModifyProperty(c.prevValue, c.unityProp, objectName, sideLocation || '', cType);
              updateCallback?.(index, c.unityProp, c.prevValue);
            });
          },
          redo: () => {
            changes.forEach(c => {
              callUnityEventModifyProperty(c.nextValue, c.unityProp, objectName, sideLocation || '', cType);
              updateCallback?.(index, c.unityProp, c.nextValue);
            });
          },
        };
      }
    );

    // Add/Remove tape action
    this._pushAddRemove = this.createAction(({ objectName, componentType, refreshCallback }) => {
      if (!objectName) return false;
      return {
        __ACTION__: 'TAPE_ADD_REMOVE',
        undo: async () => {
          disableGameObject({
            propInfoCommandDatas: [{ objectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          enableGameObject({
            propInfoCommandDatas: [{ objectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });

    // Delete tape action (opposite of add)
    this._pushDelete = this.createAction(({ objectName, componentType, refreshCallback }) => {
      if (!objectName) return false;
      return {
        __ACTION__: 'TAPE_DELETE',
        undo: async () => {
          // Undo delete = restore the tape (enable)
          enableGameObject({
            propInfoCommandDatas: [{ objectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          // Redo delete = delete again (disable)
          disableGameObject({
            propInfoCommandDatas: [{ objectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });

    // Replace tape action (disable old, enable new)
    this._pushReplace = this.createAction(({ 
      oldObjectName, 
      newObjectName, 
      componentType, 
      refreshCallback 
    }) => {
      if (!oldObjectName || !newObjectName) return false;
      return {
        __ACTION__: 'TAPE_REPLACE',
        undo: async () => {
          console.log('[UndoRedoTape][UNDO REPLACE] Disabling new:', newObjectName, 'Enabling old:', oldObjectName);
          
          // First: Disable the new tape
          disableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 50));
          
          // Second: Enable the old tape
          enableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Refresh after both operations
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
        redo: async () => {
          console.log('[UndoRedoTape][REDO REPLACE] Disabling old:', oldObjectName, 'Enabling new:', newObjectName);
          
          // First: Disable the old tape
          disableGameObject({
            propInfoCommandDatas: [{ objectName: oldObjectName }],
          });
          
          // Small delay to ensure Unity processes
          // await new Promise(resolve => setTimeout(resolve, 50));
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Second: Enable the new tape
          enableGameObject({
            propInfoCommandDatas: [{ objectName: newObjectName }],
          });
          await waitForEventOnce(UnityEvent.OnCurrentComponentsUpdate);
          
          // Refresh after both operations
          refreshCallback?.()
          await waitForEventOnce(UnityEvent.OnReceiveCurrentComponents);
        },
      };
    });
  }

  startPropertyChange({ objectName, unityProp, initialValue, index }) {
    const key = `${objectName}__${unityProp}`;
    this.pending.set(key, {
      objectName,
      unityProp,
      initialValue: toUnityFixed(initialValue, 3),
      index,
    });
  }

  updatePendingChange({ objectName, unityProp, currentValue }) {
    const key = `${objectName}__${unityProp}`;
    const item = this.pending.get(key);
    if (!item) return;
    item.lastValue = toUnityFixed(currentValue, 3);
  }

  endPropertyChange({
    objectName,
    unityProp,
    finalValue,
    componentType,
    index,
    updateCallback,
    isPosition,
  }) {
    const key = `${objectName}__${unityProp}`;
    const item = this.pending.get(key);
    if (!item) return;
    const prevValue = item.initialValue;
    const nextValue = toUnityFixed(finalValue, 3);
    this._pushSingle({
      prevValue,
      nextValue,
      unityProp,
      objectName,
      componentType,
      sideLocation: '',
      index,
      updateCallback,
      isPosition,
    });
    this.pending.delete(key);
  }

  addMultiPropertyAction({ index, objectName, componentType, changes, updateCallback }) {
    this._pushMulti({
      changes,
      objectName,
      componentType,
      sideLocation: '',
      index,
      updateCallback,
    });
  }
  addTapeAction({ objectName, componentType, refreshCallback }) {
    this._pushAddRemove({
      objectName,
      componentType,
      refreshCallback,
    });
    undoRedo.pause();
  }
  
  addTapeDeleteAction({ objectName, componentType, refreshCallback }) {
    this._pushDelete({
      objectName,
      componentType,
      refreshCallback,
    });
  }
  
  addTapeReplaceAction({ oldObjectName, newObjectName, componentType, refreshCallback }) {
    this._pushReplace({
      oldObjectName,
      newObjectName,
      componentType,
      refreshCallback,
    });
  }

  clearAll() {
    this.pending.clear();
  }
}
